import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ValidateIcon,
  Refresh as RefreshIcon,
  ExitToApp as AbsenceIcon,
  PictureAsPdf as PdfIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { autorisationsAbsenceAPI, employeesAPI, auditLogsAPI } from '../../../services/api';
import {
  AutorisationAbsence,
  AutorisationAbsenceStats,
  STATUT_AUTORISATION_OPTIONS,
  getStatutAutorisationLabel,
  getStatutAutorisationColor,
} from '../../../types/autorisationAbsence';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
// generateAutorisationAbsencePDF is dynamically imported in handleGeneratePDF
// so the jspdf chunk only loads when the user requests a PDF.
import AutorisationAbsenceFormDialog from './AutorisationAbsenceFormDialog';
import AutorisationAbsenceValidationDialog from './AutorisationAbsenceValidationDialog';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const HR_COLOR = '#00897B';

const AUTORISATION_FILTER_SCHEMA = {
  ref_autorisation: { type: 'text',         param: 'ref_autorisation' },
  created_at:       { type: 'dateRange',    paramFrom: 'created_at_from', paramTo: 'created_at_to' },
  employee_id:      { type: 'autocomplete', param: 'employee_id', transform: (v: string) => Number(v) },
  date_debut:       { type: 'dateRange',    paramFrom: 'date_debut_from', paramTo: 'date_debut_to' },
  date_fin:         { type: 'dateRange',    paramFrom: 'date_fin_from',   paramTo: 'date_fin_to' },
  nbr_jours:        { type: 'numberRange',  paramMin: 'nbr_jours_min',    paramMax: 'nbr_jours_max' },
  statut:           { type: 'select',       param: 'statut' },
} as const;

const AutorisationsAbsenceManagement: React.FC = () => {
  const [autorisations, setAutorisations] = useState<AutorisationAbsence[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState<AutorisationAbsenceStats | null>(null);

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [selectedAutorisation, setSelectedAutorisation] = useState<AutorisationAbsence | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [autorisationToDelete, setAutorisationToDelete] = useState<AutorisationAbsence | null>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [autorisationToValidate, setAutorisationToValidate] = useState<AutorisationAbsence | null>(null);

  // Employees for dropdown
  const [employees, setEmployees] = useState<EmployeeDropdownItem[]>([]);

  const { showSuccess, showError } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    AUTORISATION_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  const fetchAutorisations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await autorisationsAbsenceAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        search: search || undefined,
        ...toQueryParams(),
      });
      setAutorisations(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, filters, showError]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await autorisationsAbsenceAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeesAPI.getAllForDropdown();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchAutorisations();
    fetchStats();
  }, [fetchAutorisations, fetchStats]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddAutorisation = () => {
    setSelectedAutorisation(null);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleEditAutorisation = (autorisation: AutorisationAbsence) => {
    setSelectedAutorisation(autorisation);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (autorisation: AutorisationAbsence) => {
    setAutorisationToDelete(autorisation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!autorisationToDelete) return;
    try {
      await autorisationsAbsenceAPI.delete(autorisationToDelete.id);
      showSuccess('Autorisation d\'absence supprimee avec succes');
      fetchAutorisations();
      fetchStats();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setAutorisationToDelete(null);
    }
  };

  const handleValidateClick = (autorisation: AutorisationAbsence) => {
    setAutorisationToValidate(autorisation);
    setValidationDialogOpen(true);
  };

  const handleSuccess = () => {
    fetchAutorisations();
    fetchStats();
  };

  const handleGeneratePDF = (autorisation: AutorisationAbsence) => {
    openPreview({
      getBlob: async () => {
        const { generateAutorisationAbsencePDF } = await import('../../../utils/pdfGenerator');
        return generateAutorisationAbsencePDF({
          id: autorisation.id,
          ref_autorisation: autorisation.ref_autorisation,
          date_debut: autorisation.date_debut,
          date_fin: autorisation.date_fin,
          nbr_jours: autorisation.nbr_jours,
          motif: autorisation.motif,
          employee: autorisation.employee ? {
            nom_complet: autorisation.employee.nom_complet,
            matricule: autorisation.employee.matricule,
            fonction: autorisation.employee.fonction,
          } : null,
        }, true) as Blob;
      },
      fileName: `Autorisation_${autorisation.ref_autorisation || autorisation.id}.pdf`,
    });
    auditLogsAPI.logPdfDownload('AutorisationAbsence', autorisation.id, `Aperçu PDF - ${autorisation.ref_autorisation}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: HR_COLOR, color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AbsenceIcon />
                <Typography variant="h6">{stats?.total_demandes || 0}</Typography>
              </Box>
              <Typography variant="body2">Total Demandes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FFA726', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_en_attente || 0}</Typography>
              <Typography variant="body2">En Attente</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#66BB6A', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_autorisees || 0}</Typography>
              <Typography variant="body2">Autorisees</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#EF5350', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_refusees || 0}</Typography>
              <Typography variant="body2">Refusees</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Rechercher un employe ou ref..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          {hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              Effacer les filtres
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Actualiser">
            <IconButton onClick={() => { fetchAutorisations(); fetchStats(); }} sx={{ color: HR_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAutorisation}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
          >
            Nouvelle Demande
          </Button>
        </Box>

        {/* Table */}
        <TableContainer sx={getModuleTableTheme('hr')}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Ref.
                    <ColumnFilter type="text" value={filters.ref_autorisation} onChange={(v) => update('ref_autorisation', v)} label="référence" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Creation
                    <ColumnFilter type="dateRange" value={filters.created_at} onChange={(v) => update('created_at', v)} label="date création" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Employe
                    <ColumnFilter
                      type="autocomplete"
                      value={filters.employee_id}
                      onChange={(v) => update('employee_id', v)}
                      label="employé"
                      color={HR_COLOR}
                      options={employees.map(e => ({ value: e.id.toString(), label: `${e.matricule || ''} ${e.nom_complet}`.trim() }))}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Debut
                    <ColumnFilter type="dateRange" value={filters.date_debut} onChange={(v) => update('date_debut', v)} label="date début" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Fin
                    <ColumnFilter type="dateRange" value={filters.date_fin} onChange={(v) => update('date_fin', v)} label="date fin" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Nbr Jours
                    <ColumnFilter type="numberRange" value={filters.nbr_jours} onChange={(v) => update('nbr_jours', v)} label="nbr jours" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Statut
                    <ColumnFilter
                      type="select"
                      value={filters.statut}
                      onChange={(v) => update('statut', v)}
                      label="statut"
                      color={HR_COLOR}
                      options={STATUT_AUTORISATION_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Chargement...</TableCell>
                </TableRow>
              ) : autorisations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Aucune demande trouvee</TableCell>
                </TableRow>
              ) : (
                autorisations.map((autorisation) => (
                  <TableRow key={autorisation.id} hover>
                    <TableCell>
                      {autorisation.ref_autorisation ? (
                        <Chip
                          label={autorisation.ref_autorisation}
                          size="small"
                          sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(autorisation.created_at)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {autorisation.employee?.nom_complet || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {autorisation.employee?.matricule}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(autorisation.date_debut)}</TableCell>
                    <TableCell>{formatDate(autorisation.date_fin)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${autorisation.nbr_jours} j`}
                        size="small"
                        sx={{ bgcolor: '#E3F2FD', fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatutAutorisationLabel(autorisation.statut)}
                        size="small"
                        color={getStatutAutorisationColor(autorisation.statut)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {autorisation.statut === 'EN_ATTENTE' && (
                          <>
                            <Tooltip title="Valider/Refuser">
                              <IconButton
                                size="small"
                                onClick={() => handleValidateClick(autorisation)}
                                sx={{ color: '#66BB6A' }}
                              >
                                <ValidateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                onClick={() => handleEditAutorisation(autorisation)}
                                sx={{ color: HR_COLOR }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {autorisation.statut === 'AUTORISEE' && (
                          <Tooltip title="Telecharger PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleGeneratePDF(autorisation)}
                              sx={{ color: '#1976D2' }}
                            >
                              <PdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(autorisation)}
                            sx={{ color: '#EF5350' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {/* Form Dialog */}
      <AutorisationAbsenceFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setFormMinimized(false);
        }}
        autorisation={selectedAutorisation}
        employees={employees}
        onSuccess={handleSuccess}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      {/* Validation Dialog */}
      <AutorisationAbsenceValidationDialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        autorisation={autorisationToValidate}
        onSuccess={handleSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Etes-vous sur de vouloir supprimer l'autorisation d'absence de ${autorisationToDelete?.employee?.nom_complet || 'cet employe'} ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel={true}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default AutorisationsAbsenceManagement;
